import type { ComponentRuntimeDefinition } from '../../catalog/index.ts';
import { z } from 'zod';
import { componentHardMinimum } from '../shared/sizingProfiles.ts';

export interface BlockLayoutCapability {
  movable: boolean;
  resizable: boolean;
  minWidth: number;
  minHeight: number;
}

const splitViewConfigSchema = z.object({
  splitRatio: z.number().min(0.1).max(0.9).default(0.5),
  leftLabel: z.string().max(100).default(''),
  rightLabel: z.string().max(100).default(''),
  labels: z.array(z.string().max(100)).max(8).default([]),
  direction: z.enum(['row', 'column', 'auto']).default('auto'),
  ratios: z.array(z.number().positive().max(10)).max(8).default([]),
}).strict();

export const splitViewDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.split-view@1',
    category: 'layout',
    title: '对照对比视图',
    description: '同类型组合容器，按 block.children 的真实尺寸在 2–8 个子块之间自适应选择行列。',
    capabilities: ['split-view', 'layout-container', 'adaptive-composite', 'multi-panel'],
    suitableFor: ['方案对比', '前后对比', 'AB对照', '多指标并列'],
    inputs: [],
    outputs: [],
    events: [],
    constraints: ['子块通过 block.children 传入', '只组合 2–8 个同类型组件', 'auto 根据子组件最小尺寸与容器尺寸从声明式候选列数中选择'],
    configSchemaId: 'probfun.split-view.config@1',
    configSchema: {
      type: 'object',
      properties: {
        splitRatio: { type: 'number', minimum: 0.1, maximum: 0.9, default: 0.5 },
        leftLabel: { type: 'string' },
        rightLabel: { type: 'string' },
        labels: { type: 'array', maxItems: 8, items: { type: 'string' }, default: [] },
        direction: { type: 'string', enum: ['row', 'column', 'auto'], default: 'auto' },
        ratios: { type: 'array', maxItems: 8, items: { type: 'number', exclusiveMinimum: 0 }, default: [] },
      },
      additionalProperties: false,
    },
    defaultConfig: {
      splitRatio: 0.5,
      leftLabel: '',
      rightLabel: '',
      labels: [],
      direction: 'auto',
      ratios: [],
    },
    examples: [
      { splitRatio: 0.5, direction: 'auto' },
      { splitRatio: 0.5, direction: 'row', ratios: [1, 1, 1] },
      { splitRatio: 0.5, direction: 'column', ratios: [1, 1] },
    ],
    notes: [
      '面板数量以 block.children 为准，只支持 2–8 个同类型子组件。',
      '不渲染浮窗小标题；方案名写在子组件自己的 config.label / title 里。',
      'direction=row 强制 n 列横排，column 强制 n 行竖排。',
      'direction=auto（默认）：从统一尺寸规则中的候选列数选择不吞内容且空白最少的网格。',
      'ratios 与 children 等长时按比例分配；两栏且未给 ratios 时仍用 splitRatio。',
      'leftLabel / rightLabel / labels 仅兼容旧配置，不再显示。',
    ],
    layout: { ...componentHardMinimum('probfun.split-view@1'), preferredAspectRatio: 2 },
    runtimeRequirements: [],
    metadata: { layoutHints: { roles: ['primary'], preferredSize: 'large', supportsCompact: true, supportsExpanded: false, canStack: false, priority: 80 } },
  },
  configSchema: splitViewConfigSchema,
  loader: () => import('./SplitViewBlock.vue').then(m => m.default),
};
